import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm, FormProvider, useWatch, useFieldArray } from "react-hook-form";
import {
  DateFilterTypesEnum,
  EntityType,
  OrganizationService,
  PaidFlowTypesEnum,
  PayeeAccountTypesEnum,
  PaymentService,
  PropertyService,
  TenantService,
  UnitService,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import DateHelper from "../../utils/DateHelper";
import { uploadPaymentReferenceDocument } from "./paymentDocumentHelpers";
import {
  buildPayablePayeeTypeOptions,
  buildPayeeAccountsFilterForType,
} from "./payeePaymentTypeHelpers";
import { UserLoginData } from "../../components/common/charts/Enums";
import InputField from "../form-fields/InputField";
import CurrencyInput from "../form-fields/CurrencyInput";
import Radio from "../form-fields/radio";
import FixedFooter from "../listings/add-listing-footer/fixed-footer";
import SelectField from "../form-fields/SelectField";
import LeftArrowTopHeader from "../form-fields/LeftArrowTopHeader";
import { Accordion, AccordionSection } from "../card/accordion";
import SimpleButton from "../buttons/Simple-button";
import DocumentUploader from "../CustomUploader/DocumentUploader";
import SvgRoundIconButton from "../buttons/Svg-Round-Icon-button";
import { DeleteRedIcon } from "../../assets/svgs/DeleteRedIcon";
import SimpleOutlineButton from "../buttons/Simple-Outline-button";
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import DynamicTabs from "../tabs/DynamicTabs";
import DuesORTransaction from "./DuesORTransaction";
import "./payment.css";
import SimpleSpinner from "../../components/loader";
import EmptyState from "../emptystate";
import { GetImages } from "../../utils/GetImages";
import { utcDateFormat } from "../../utils/helper";
import { URLS } from "../../constants/constants";
import RadioGroup from "../form-fields/RadioGroup";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";

// Type definitions
type FormValues = {
  paymentMode?: string;
  PayeeTypeId?: string;
  PayeeId?: string;
  tenantId?: string;
  AttachPaymentId?: string;
  PropertyId?: string;
  UnitId?: string;
  documentfile?: File;
  accountNumber?: string;
  InvoiceNo?: string;
  payments: {
    PaymentType: string;
    Amount: string;
    DueDate: string;
    GracePeriod: string;
    Description: string;
  }[];
};

type AddPayeeFormValues = {
  payeeName: string;
  AccountTypeId: string;
};

interface AccordionState {
  basicDetailsOpen: boolean;
  paymentInfoOpen: boolean;
  basicDetailsComplete: boolean;
}

interface ModalState {
  payee: boolean;
  viewAll: boolean;
}

interface ApiData {
  payeeAccounts: any[];
  tenants: any[];
  properties: any[];
  units: any[];
  paymentTypes: any[];
  accountPayeeTypes: any[];
  dues: any[];
  transactions: any[];
}

const getBasicDetailFieldsToValidate = (
  paymentMode: string,
  attachPaymentId: string | undefined,
  effectiveisEntityProp: boolean,
): Array<keyof FormValues> => {
  const fieldsToValidate: Array<keyof FormValues> = ["paymentMode", "accountNumber", "InvoiceNo"];

  if (paymentMode === "1") {
    fieldsToValidate.push("PayeeTypeId", "PayeeId", "AttachPaymentId", "InvoiceNo");
    if (attachPaymentId === "Property" && !effectiveisEntityProp) fieldsToValidate.push("PropertyId");
    if (attachPaymentId === "Unit") fieldsToValidate.push("UnitId");
  } else if (paymentMode === "2") {
    fieldsToValidate.push("tenantId");
  }

  return fieldsToValidate;
};

// Constants
const initialAccordionState: AccordionState = {
  basicDetailsOpen: true,
  paymentInfoOpen: false,
  basicDetailsComplete: false,
};

const initialModalState: ModalState = {
  payee: false,
  viewAll: false,
};

const initialApiData: ApiData = {
  payeeAccounts: [],
  tenants: [],
  properties: [],
  units: [],
  paymentTypes: [],
  accountPayeeTypes: [],
  dues: [],
  transactions: [],
};

const thingsToKnow = [
  { title: "", points: [{ text: "The entire property is designed for one household, meaning there are no multiple units within the structure." }] },
  { title: "", points: [{ text: "The owner has complete control over the property." }] },
  { title: "", points: [{ text: "Single-family homes provide more privacy, space, and independence than multi-family properties." }] },
];

const accountOptions = [
  { Id: "Organization", Name: "Organization" },
  { Id: "Property", Name: "Property" },
  { Id: "Unit", Name: "Unit" },
];

interface AddPaymentProps {
  fetchData?: any;
  isEntityProp?: boolean;
  entityIdProp?: number;
  entityTypeIdProp?: any;
}

const AddPayment = ({ fetchData, isEntityProp, entityIdProp, entityTypeIdProp }: AddPaymentProps) => {
  // State management
  const [resetCounter, setResetCounter] = useState(0);
  const [modals, setModals] = useState<ModalState>(initialModalState);
  const [data, setData] = useState<ApiData>(initialApiData);
  const [isLoading, setIsLoading] = useState(false);
  const [isDuesLoading, setIsDuesLoading] = useState(false);
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(false);

  const [isValid, setIsValid] = useState(false);
  const [accordionState, setAccordionState] = useState<AccordionState>(initialAccordionState);
  const navigate = useNavigate();
  const location = useLocation();
  const currencySymbol = useCurrencySymbol();

  // const receivableInvoiceNumber = crypto.randomUUID().replaceAll('-', '').toUpperCase()

  // Extract props from location.state if not passed as props
  const {
    isEntityProp: stateisEntityProp,
    entityIdProp: stateentityIdProp,
    entityTypeIdProp: stateentityTypeIdProp
  } = location.state || {};

  const effectiveisEntityProp = isEntityProp ?? stateisEntityProp ?? false;
  const effectiveentityIdProp = entityIdProp ?? stateentityIdProp;
  const effectiveentityTypeIdProp = stateentityTypeIdProp;

  const organization_id = Number(sessionStorage.getItem(UserLoginData.organization_id));
  const isTenantEntity = effectiveisEntityProp && effectiveentityTypeIdProp === EntityType.Tenant;

  // Form management
  const paymentMethods = useForm<FormValues>({
    defaultValues: {
      // UPDATED: Default to "1" (Payable) unless it's strictly a Tenant entity ("2")
      paymentMode: isTenantEntity ? "2" : "1",
      PayeeTypeId: "",
      PayeeId: "",
      tenantId: isTenantEntity ? effectiveentityIdProp?.toString() || "" : "",
      AttachPaymentId: "",
      PropertyId: !isTenantEntity && effectiveentityIdProp ? effectiveentityIdProp.toString() : "",
      UnitId: "",
      documentfile: null,
      accountNumber: "",
      InvoiceNo: "",
      payments: [
        {
          PaymentType: "",
          Amount: "",
          DueDate: "",
          GracePeriod: "",
          Description: "",
        },
      ],
    },
    mode: "onBlur",
  });

  const payeeMethods = useForm<AddPayeeFormValues>({
    defaultValues: {
      payeeName: "",
      AccountTypeId: "",
    },
    mode: "onBlur",
  });

  const {
    handleSubmit: handlePaymentSubmit,
    control,
    trigger,
    setValue,
    formState: { errors, isDirty, isValid: isFormValid },
  } = paymentMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "payments",
  });

  // Form watchers
  const paymentMode = useWatch({ control, name: "paymentMode" });
  const PayeeTypeId = useWatch({ control, name: "PayeeTypeId" });
  const PayeeId = useWatch({ control, name: "PayeeId" });
  const AttachPaymentId = useWatch({ control, name: "AttachPaymentId" });

  // API Data Fetching
  const fetchInitialData = useCallback(async () => {
    try {
      const [
        tenants,
        properties,
        units,
        accountPayeeTypes,
      ] = await Promise.all([
        apiHelper(
          TenantService.getApiTenantOrganizationlookup,
          { showNotification: false },
          organization_id
        ),
        apiHelper(
          PropertyService.getApiPropertyOrganizationlookup,
          { showNotification: false },
          organization_id
        ),
        apiHelper(
          UnitService.getApiUnitOrganizationlookup,
          { showNotification: false },
          organization_id
        ),
        apiHelper(
          PaymentService.getApiPayeeAccountPayeeTypes,
          { showNotification: false }
        ),
      ]);

      setData((prev) => ({
        ...prev,
        tenants: tenants?.data?.success || [],
        properties: properties?.data?.success || [],
        units: units?.data?.success || [],
        accountPayeeTypes: accountPayeeTypes?.data?.success || [],
      }));
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load required data",
        type: NotificationType.Failure,
      });
    }
  }, [organization_id]);

  const fetchPayeeAccountsForType = useCallback(
    async (payeeTypeId?: string) => {
      try {
        const res = await apiHelper(
          PaymentService.getApiPayeeAccountOrganizationlist,
          { showNotification: false },
          organization_id,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          buildPayeeAccountsFilterForType(payeeTypeId)
        );

        setData((prev) => ({
          ...prev,
          payeeAccounts: res?.data?.success || [],
        }));
      } catch (error) {
        PubSub.publish(TopicsEnum.Notification.toString(), {
          message: "Failed to load payees",
          type: NotificationType.Failure,
        });
      }
    },
    [organization_id]
  );

  const fetchPaymentTypes = useCallback(async () => {
    try {
      const payeeAccountTypeId =
        paymentMode === "2"
          ? PayeeAccountTypesEnum.Tenant
          : paymentMode === "1"
            ? Number(PayeeTypeId) || ""
            : "";

      if (payeeAccountTypeId) {
        const res = await apiHelper(
          PaymentService.getApiPayeeAccountPaymentTypes,
          { showNotification: false },
          payeeAccountTypeId
        );

        if (res?.data?.success) {
          setData((prev) => ({
            ...prev,
            paymentTypes: Array.from(new Map(res.data.success.map((item) => [item.id, item])).values()) || [],
          }));
        }
      } else {
        setData((prev) => ({ ...prev, paymentTypes: [] }));
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch Payment types",
        type: NotificationType.Failure,
      });
    }
  }, [paymentMode, PayeeTypeId]);

  const fetchPayeeDues = useCallback(async () => {
    if (!PayeeId) return;

    setIsDuesLoading(true);
    try {
      const res = await apiHelper(
        PaymentService.getApiPaymentDueAccountPayeelist,
        { showNotification: false },
        PayeeId,
        undefined, undefined, undefined, undefined, undefined, undefined, undefined
      );

      if (res?.data?.success) {
        setData((prev) => ({
          ...prev,
          dues: res.data.success || [],
        }));
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch dues",
        type: NotificationType.Failure,
      });
    } finally {
      setIsDuesLoading(false);
    }
  }, [PayeeId]);

  const fetchTransactions = useCallback(async () => {
    if (!PayeeId) return;

    setIsTransactionsLoading(true);
    try {
      const res = await apiHelper(
        OrganizationService.getApiOrganizationTransactionOrganizationlist,
        { showNotification: false },
        organization_id,
        5,
        1,
        undefined,
        undefined,
        undefined,
        `PayeeAccountId = ${PayeeId}`,
        "PaymentDate desc"
      );

      if (res?.data?.success) {
        setData((prev) => ({
          ...prev,
          transactions: res.data.success || [],
        }));
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch transactions",
        type: NotificationType.Failure,
      });
    } finally {
      setIsTransactionsLoading(false);
    }
  }, [PayeeId, organization_id]);

  // Event Handlers
  const handleNextClick = useCallback(async () => {
    const fieldsToValidate = getBasicDetailFieldsToValidate(
      paymentMode,
      AttachPaymentId,
      effectiveisEntityProp,
    );
    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      setAccordionState({
        basicDetailsOpen: false,
        paymentInfoOpen: true,
        basicDetailsComplete: true,
      });
    } else {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Please fill all required fields in Basic Details",
        type: NotificationType.Failure,
      });
    }
  }, [paymentMode, AttachPaymentId, trigger, effectiveisEntityProp]);

  const handleInvalidPaymentSubmit = useCallback(async () => {
    const basicFields = getBasicDetailFieldsToValidate(
      paymentMode,
      AttachPaymentId,
      effectiveisEntityProp,
    );
    const basicValid = await trigger(basicFields);

    if (!basicValid) {
      setAccordionState({
        basicDetailsOpen: true,
        paymentInfoOpen: false,
        basicDetailsComplete: false,
      });
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Please fill all required fields in Basic Details",
        type: NotificationType.Failure,
      });
      return;
    }

    const paymentValid = await trigger("payments");
    if (!paymentValid) {
      setAccordionState({
        basicDetailsOpen: false,
        paymentInfoOpen: true,
        basicDetailsComplete: true,
      });
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Please fill all required payment details",
        type: NotificationType.Failure,
      });
    }
  }, [paymentMode, AttachPaymentId, effectiveisEntityProp, trigger]);

  const toggleModal = useCallback((modalName: keyof ModalState) => {
    setModals((prev) => ({ ...prev, [modalName]: !prev[modalName] }));
  }, []);

  const toggleAccordion = useCallback((section: keyof AccordionState) => {
    setAccordionState((prev) => ({
      ...prev,
      basicDetailsOpen: section === "basicDetailsOpen" ? !prev.basicDetailsOpen : false,
      paymentInfoOpen: section === "paymentInfoOpen" ? !prev.paymentInfoOpen : false,
      basicDetailsComplete: prev.basicDetailsComplete,
    }));
  }, []);

  const onSubmit = useCallback(async (formData: FormValues, actionType: "PostPayment" | "SavePayment" | "SaveAddMore") => {
    setIsLoading(true);

    try {
      // Common payment processing function
      const processPayments = async (paymentConfig: {
        entityId: number;
        entityTypeId: EntityType;
        paidFlowTypeId: PaidFlowTypesEnum;
        isOrganizationAccount?: boolean;
        actionType: string;
      }) => {
        let payeeAccountId = 0;

        await Promise.all(
          formData.payments.map(async (item, index: any) => {
            // Get payee account if needed for organization
            payeeAccountId = Number(formData.PayeeId) || 0;

            if (paymentConfig.isOrganizationAccount) {
              const response = await apiHelper(
                PaymentService.getApiPayeeAccountOrganizationlist,
                { showNotification: false },
                organization_id, undefined, undefined, undefined, undefined, undefined,
                `IsOrganizationAccount==true`
              );

              // Fixed: Use the first organization account rather than trying to match by index
              payeeAccountId = response?.data?.success?.[index]?.id || payeeAccountId;
            }

            // Inside onSubmit function -> processPayments -> paymentObj creation

            const paymentObj = {
              amount: Number(item.Amount.toString().replace(/,/g, "")),
              description: item.Description,
              dueDate: paymentConfig.paidFlowTypeId === PaidFlowTypesEnum.Receiveable
                ? utcDateFormat(item.DueDate)
                : new Date(item.DueDate).toISOString(),
              entityId: paymentConfig.entityId,
              entityTypeId: paymentConfig.entityTypeId,
              payeeAccountId,
              gracePeriodInDays: Number(item.GracePeriod) || 0,
              paymentTypeId: Number(item.PaymentType) || 0,
              paidFlowTypeId: paymentConfig.paidFlowTypeId,
              accountNumber: formData.accountNumber || "",

              referenceNumber: formData.InvoiceNo
            };

            // Submit payment
            const paymentResponse = await apiHelper(
              PaymentService.postApiPaymentDueAccount,
              {
                title: "Payment Added",
                successMessage: "Payment added successfully",
                successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
                showNotification: true
              },
              paymentObj
            );

            // Handle document upload if exists
            if (formData.documentfile && paymentResponse?.data?.success) {
              await uploadPaymentReferenceDocument(
                paymentResponse.data.success.id,
                formData.documentfile,
              );
            }
          }));
        if (actionType === "PostPayment" && formData.paymentMode === "1") {
          resetForms();
          navigate(`/payment/settle-payables-payment/${payeeAccountId}`);
        }
        else if (actionType === "PostPayment" && formData.paymentMode === "2") {
          resetForms();
          navigate(`/payment/settle-receivables-payment/${formData.tenantId}`);
        }
        else if (actionType === "SaveAddMore" && formData.paymentMode === "1") {

          await resetForms();
          // setValue("PayeeId", formData.PayeeId);
          // setValue("paymentMode", formData.paymentMode);
        }
        else if (actionType === "SaveAddMore" && formData.paymentMode === "2") {
          await resetForms();
          setValue("tenantId", formData.tenantId);
          setValue("paymentMode", formData.paymentMode);
        }
        else {


          // Navigate to the referrer page or default
          const referrer = location.state?.from || "/payment";
          navigate(referrer, { state: { activeTab: location.state?.activeTab } });
        }
      };

      // Payment mode 1 (Payable)
      if (formData.paymentMode === "1") {
        const { entityId, entityTypeId } = (() => {
          // If coming from entity page, use the passed props
          if (effectiveisEntityProp && effectiveentityIdProp && effectiveentityTypeIdProp) {
            return {
              entityId: Number(effectiveentityIdProp),
              entityTypeId: effectiveentityTypeIdProp // Use the passed entityTypeId
            };
          }

          // Otherwise, determine based on form values
          if (formData.PropertyId && Number(formData.PropertyId) > 0) {
            return {
              entityId: Number(formData.PropertyId),
              entityTypeId: EntityType.Property
            };
          }
          if (formData.UnitId && Number(formData.UnitId) > 0) {
            return {
              entityId: Number(formData.UnitId),
              entityTypeId: EntityType.Unit
            };
          }
          return {
            entityId: Number(organization_id),
            entityTypeId: EntityType.Organization
          };
        })();

        await processPayments({
          entityId,
          entityTypeId,
          paidFlowTypeId: PaidFlowTypesEnum.Payable,
          actionType
        });
      }
      // Payment mode 2 (Receivable)
      else if (formData.paymentMode === "2") {
        if (!formData.tenantId || Number(formData.tenantId) <= 0) {
          throw new Error("Tenant ID is required for receivable payments");
        }

        await processPayments({
          entityId: Number(formData.tenantId),
          entityTypeId: EntityType.Tenant,
          paidFlowTypeId: PaidFlowTypesEnum.Receiveable,
          isOrganizationAccount: true,
          actionType
        });
      }
    }
    catch (error: any) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: error.message || "Failed to submit payment",
        type: NotificationType.Failure,
      });
    } finally {
      setIsLoading(false);
    }
  }, [navigate, organization_id, paymentMethods, payeeMethods, fetchData, location.state, setValue, effectiveisEntityProp, effectiveentityIdProp, effectiveentityTypeIdProp]);

  // Extracted form reset function
  const resetForms = () => {
    paymentMethods.reset({
      paymentMode: "1", // Reset to Payable by default
      PayeeTypeId: "",
      PayeeId: "",
      tenantId: "",
      AttachPaymentId: "",
      PropertyId: effectiveentityIdProp?.toString() || "",
      UnitId: "",
      documentfile: null,
      accountNumber: "",
      InvoiceNo: "",
      payments: [
        {
          PaymentType: "",
          Amount: "",
          DueDate: "",
          GracePeriod: "",
          Description: "",
        },
      ],
    });
    payeeMethods.reset();
    setAccordionState((prev) => ({
      ...prev,
      basicDetailsOpen: true,
      paymentInfoOpen: false,
      basicDetailsComplete: false,
    }));
    setResetCounter((prev) => prev + 1);
  };

  const onAddPayeeSubmit = useCallback(
    async (formData: AddPayeeFormValues) => {
      setIsLoading(true);
      try {
        const response = await apiHelper(
          PaymentService.postApiPayeeAccount,
          {
            showNotification: true,
            successMessage: "Payee Account Added Successfully",
          },
          {
            accountName: formData.payeeName,
            organizationId: organization_id,
            payeeAccountTypeId: Number(formData.AccountTypeId),
          }
        );

        if (response?.data?.success) {
          await fetchPayeeAccountsForType(formData.AccountTypeId);

          setValue("PayeeTypeId", formData.AccountTypeId, { shouldValidate: true });
          setValue("PayeeId", String(response.data.success.id), { shouldValidate: true });
          toggleModal("payee");
          payeeMethods.reset();
        }
      } catch (error) {
        PubSub.publish(TopicsEnum.Notification.toString(), {
          message: "Unable to Add Payee Account",
          type: NotificationType.Failure,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [organization_id, toggleModal, payeeMethods, setValue, fetchPayeeAccountsForType]
  );

  const onCancel = useCallback(() => navigate(-1), [navigate]);

  const addPaymentRow = useCallback(() => {
    append({
      PaymentType: "",
      Amount: "",
      DueDate: "",
      GracePeriod: "",
      Description: "",
    });
  }, [append]);

  const removePaymentRow = useCallback((index: number) => {
    remove(index);
  }, [remove]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      if (tabId === "dues") {
        fetchPayeeDues();
      } else if (tabId === "transactions") {
        fetchTransactions();
      }
    },
    [fetchPayeeDues, fetchTransactions]
  );

  // Effects
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    if (paymentMode === "1") {
      fetchPayeeAccountsForType(PayeeTypeId);
    }
  }, [paymentMode, PayeeTypeId, fetchPayeeAccountsForType]);

  useEffect(() => {
    fetchPaymentTypes();
  }, [fetchPaymentTypes]);

  useEffect(() => {
    if (Number(PayeeId) > 0) {
      fetchPayeeDues();
    }
  }, [PayeeId, fetchPayeeDues]);

  // Memoized options
  const payeeAccountsOptions = useMemo(() => data.payeeAccounts.map((t: any) => ({ Id: t.id, Name: t.accountName })), [data.payeeAccounts]);
  const tenantOptions = useMemo(() => data.tenants.map((t: any) => ({ Id: t.id, Name: t.name })), [data.tenants]);
  const propertyOptions = useMemo(() => data.properties.map((t: any) => ({ Id: t.id, Name: t.name })), [data.properties]);
  const unitOptions = useMemo(() => data.units.map((t: any) => ({ Id: t.id, Name: t.name })), [data.units]);
  const paymentTypeOptions = useMemo(() => data.paymentTypes.map((t: any) => ({ Id: t.id, Name: t.name })), [data.paymentTypes]);
  const accountPayeeTypesOptions = useMemo(() => data.accountPayeeTypes.map((t: any) => ({ Id: t.id, Name: t.name })), [data.accountPayeeTypes]);
  const payablePayeeTypeOptions = useMemo(() => buildPayablePayeeTypeOptions(accountPayeeTypesOptions), [accountPayeeTypesOptions]);

  const handlePayeeTypeChange = useCallback(() => {
    // Payee Type cascades into Select Payee (list refetched via the effect above) and
    // Payment Types (refetched via fetchPaymentTypes) -- both depended on whatever payee was
    // previously selected, so clear it to avoid a stale, no-longer-listed selection lingering.
    setValue("PayeeId", "", { shouldValidate: false });
  }, [setValue]);

  // Render components
  const renderBasicDetails = () => (
    <Accordion>
      <AccordionSection
        title="Basic Details"
        isOpen={accordionState.basicDetailsOpen}
        onToggle={() => toggleAccordion("basicDetailsOpen")}
      >
        <div className="accordion-body">
          <div className="row">
            <div className="col-md-6">
              <form>
                <div className="row  align-items-center">
                  <div className="col-12 mb-2">
                    {/* Show Payables if not an entity OR if entity is NOT a Tenant */}
                    <RadioGroup
                      label="Payment Type"
                      required
                      requiredMessage="Payment type is required"
                    >
                      {(!effectiveisEntityProp || !isTenantEntity) && (
                        <Radio
                          name="paymentMode"
                          value="1"
                          label="Payables"
                        />
                      )}

                      {(!effectiveisEntityProp || isTenantEntity) && (
                        <Radio
                          name="paymentMode"
                          value="2"
                          label="Receivables"
                        />
                      )}
                    </RadioGroup>
                  </div>

                  {paymentMode === "1" && (
                    <>
                      <div className="col-md-6">
                        <SelectField
                          placeholderText="Select Payee Type"
                          name="PayeeTypeId"
                          label="Payee Type"
                          required
                          requiredMessage="Payee Type is required"
                          options={payablePayeeTypeOptions}
                          disabled={isLoading}
                          key={`payee-type-${resetCounter}`}
                          isSearchable={true}
                          onChange={handlePayeeTypeChange}
                        />
                      </div>
                      <div className="col-md-6">
                        <SelectField
                          placeholderText="Select Payee"
                          name="PayeeId"
                          label="Select Payee"
                          required
                          requiredMessage="Select Payee is required"
                          options={payeeAccountsOptions}
                          disabled={isLoading || !PayeeTypeId}
                          key={resetCounter}
                          isSearchable={true}

                        />
                        {Number(PayeeId) > 0 && (
                          <span
                            onClick={() => toggleModal("viewAll")}
                            className="select_payee_text_Primary d-block mt-1"
                          >
                            View All Dues & Transactions
                          </span>
                        )}
                      </div>
                      {effectiveisEntityProp ? (
                        <div className="col-md-6">
                          <SelectField
                            className="attachedPaymentSelect"
                            name="AttachPaymentId"
                            label="Attach Payment To"
                            options={accountOptions}
                            value={
                              effectiveentityTypeIdProp === EntityType.Property
                                ? "Property"
                                : effectiveentityTypeIdProp === EntityType.Unit
                                  ? "Unit"
                                  : "Organization"
                            }
                            disabled
                          />
                        </div>
                      ) : (
                        <div className="col-md-6">
                          <SelectField
                            placeholderText="Select an Option"
                            name="AttachPaymentId"
                            label="Attach Payment To"
                            required
                            requiredMessage="Attach Payment To is required"
                            options={accountOptions}
                            disabled={isLoading}

                          />
                        </div>
                      )}

                      {effectiveisEntityProp && effectiveentityTypeIdProp === EntityType.Property && (
                        <div className="col-md-6">
                          <SelectField
                            name="PropertyId"
                            label="Property"
                            options={propertyOptions}
                            value={effectiveentityIdProp?.toString()}
                            disabled
                            isSearchable={true}
                          />
                        </div>
                      )}

                      {effectiveisEntityProp && effectiveentityTypeIdProp === EntityType.Unit && (
                        <div className="col-md-6">
                          <SelectField
                            name="UnitId"
                            label="Unit"
                            options={unitOptions}
                            value={effectiveentityIdProp?.toString()}
                            disabled
                            isSearchable={true}
                          />
                        </div>
                      )}

                      {AttachPaymentId === "Property" && !effectiveisEntityProp && (
                        <div className="col-md-6">
                          <SelectField
                            placeholderText="Select Property"
                            name="PropertyId"
                            label="Property"
                            required
                            requiredMessage="Property is required"
                            options={propertyOptions}
                            disabled={isLoading}
                            isSearchable={true}
                          />
                        </div>
                      )}
                      {AttachPaymentId === "Unit" && !effectiveisEntityProp && (
                        <div className="col-md-6">
                          <SelectField
                            placeholderText="Select Unit"
                            name="UnitId"
                            label="Unit"
                            required
                            requiredMessage="Unit is required"
                            options={unitOptions}
                            disabled={isLoading}
                            isSearchable={true}
                          />
                        </div>
                      )}
                    </>
                  )}
                  {paymentMode === "2" && (
                    <div className="col-md-6">
                      <SelectField
                        placeholderText="Select Tenant"
                        name="tenantId"
                        label="Tenant"
                        required
                        requiredMessage="Select Tenant is required"
                        options={tenantOptions}
                        disabled={isLoading || isTenantEntity}
                        isSearchable={true}
                      />
                    </div>
                  )}

                  <div className="col-12">
                    <div className="row">
                      <div className="col-md-6 mt-3">
                        <InputField
                          name="accountNumber"
                          label="Account Number"
                          required
                          requiredMessage="Account Number is required"
                          type="text"
                          placeholder="Enter Account Number"
                          validation={{
                            required: "Account number is required",
                            minLength: {
                              value: 1,
                              message: "Account number is required",
                            },
                            maxLength: {
                              value: 50,
                              message: "Account number cannot exceed 50 characters",
                            },
                          }}
                        />
                      </div>
                          <div className="col-md-6 mt-3">
                            <InputField
                              name="InvoiceNo"
                              label="Invoice No./Bill No."
                              type="text"
                              placeholder="Enter Invoice No./Bill No."
                              required
                              requiredMessage="Invoice No./Bill No. is required"
                              validation={{
                                required: "Invoice No./Bill No. is required",
                                minLength: {
                                  value: 1,
                                  message: "Invoice No./Bill No. is required",
                                },
                                maxLength: {
                                  value: 100,
                                  message: "Invoice No./Bill No. cannot exceed 100 characters",
                                },
                              }}
                            />
                          </div>
                    </div>
                  </div>

                  <div className="col-md-12 mt-3">
                    <div className="form-group">
                      <label className="fw-600 black">Upload Bill / Invoice <span className="optionalLabel">(optional)</span> </label>
                      <DocumentUploader name="documentfile" maxSizeMB={5} resetTrigger={resetCounter} />
                    </div>
                  </div>

                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="footer-wrapper">
          <SimpleButton type="button" label="Next" style={{ fontSize: 13 }} onClick={handleNextClick} />
        </div>
      </AccordionSection>
    </Accordion>
  );

  const renderPaymentInfo = () => (
    <Accordion>
      <AccordionSection
        title="Payment Information"
        isDisabled={false}
        isOpen={accordionState.paymentInfoOpen}
        onToggle={() => toggleAccordion("paymentInfoOpen")}
      >
        <div className="accordion-body">
          {fields.map((field, index) => (
            <div className="payment-row" key={field.id}>
              <div className="payment-field">
                <SelectField
                  placeholderText="Select payment type"
                  name={`payments.${index}.PaymentType`}
                  label={index === 0 ? "Payment Type" : ""}
                  required={true}
                  requiredMessage="Payment Type is required"
                  options={paymentTypeOptions}
                  isAsterisk={index === 0}
                  disabled={isLoading}
                />
              </div>
              <div className="payment-field">
                <CurrencyInput
                  name={`payments.${index}.Amount`}
                  label={index === 0 ? `Amount (${currencySymbol})` : ""}
                  required={true}
                  placeholder="Enter Amount"
                  requiredMessage="Amount is required"
                  disabled={isLoading}
                />
              </div>
              <div className="payment-field">
                <InputField
                  name={`payments.${index}.DueDate`}
                  label={index === 0 ? "Due Date" : ""}
                  type="date"
                  isAsterisk={index === 0}
                  placeholder="Enter Due Date"
                  required={true}
                  requiredMessage="Due Date is required"
                />
              </div>
              <div className="payment-field">
                <InputField
                  name={`payments.${index}.GracePeriod`}
                  label={index === 0 ? "Grace Period (Days)" : ""}
                  type="number"
                  required={true}
                  isAsterisk={index === 0}
                  placeholder="Enter Grace Period"
                  requiredMessage="Grace period is required"
                  validation={{
                    min: { value: 0, message: "Grace period cannot be negative" },
                    maxLength: { value: 2, message: "Grace period cannot exceed 99 days" },
                  }}
                />
              </div>
              <div className="payment-field description-field">
                <InputField
                  name={`payments.${index}.Description`}
                  label={index === 0 ? "Description" : ""}
                  type="text"
                  placeholder="Enter description"
                  required={true}
                  isAsterisk={index === 0}
                  requiredMessage="Description is required"
                />
              </div>
              <div className="payment-action">
                {/* Dummy Label to match InputField label height */}
                <div style={{ height: '21px', marginBottom: '0.5rem' }}></div>

                {fields.length > 1 && (
                  <SvgRoundIconButton
                    style={{ width: 40, background: "#FDE8E8" }}
                    Icon={<DeleteRedIcon fill="#E02424" width="22" height="22" />}
                    onClick={() => removePaymentRow(index)}
                  />
                )}
              </div>
            </div>
          ))}
          <div className="d-flex align-items-center gap-2 mt-3">
            <SimpleOutlineButton
              type="button"
              label="Add More"
              onClick={addPaymentRow}
              isDisabled={fields.length >= 5}
            />
            {fields.length >= 5 && (
              <p className="f-12 mb-0 text-danger">
                Maximum 5 payments allowed
              </p>
            )}
          </div>
        </div>
      </AccordionSection>
    </Accordion>
  );

  const renderDuesContent = () => {
    if (isDuesLoading) {
      return (
        <div className="loading-card">
          <SimpleSpinner myClassName="Icon40 mt-5" />
        </div>
      );
    }
    return data.dues.length === 0 ? (
      <EmptyState heading="No Due Payments" icon={GetImages.DuePayments} isButton={false} isBorder={false} preserveIconColors />
    ) : (
      data.dues.map((item) => <DuesORTransaction key={item.id} item={item} />)
    );
  };

  const renderTransactionsContent = () => {
    if (isTransactionsLoading) {
      return (
        <div className="loading-card">
          <SimpleSpinner myClassName="Icon40 mt-5" />
        </div>
      );
    }
    return data.transactions.length === 0 ? (
      <EmptyState heading="No Transactions" icon={GetImages.DuePayments} isButton={false} isBorder={false} preserveIconColors />
    ) : (
      data.transactions.map((item) => <DuesORTransaction key={item.id} item={item} />)
    );
  };

  return (
    <>
      <div className="d-flex flex-column gap-3 full-page-content create-payment-container">


        <LeftArrowTopHeader title="Add Payment" backPath="/payment" />
        <FormProvider {...paymentMethods}>
          <Card className="no-bg p-0">
            <div className="d-flex flex-column gap-3">
              {renderBasicDetails()}
              {renderPaymentInfo()}
            </div>
          </Card>
          <FixedFooter
            ShowCancelButton={false}
            ShowSavePostPaymentButton={true}
            SavePostPaymentbuttonLabel="Save & Post Payment"
            onSubmitPostPayment={handlePaymentSubmit(
              (data) => onSubmit(data, "PostPayment"),
              () => {
                void handleInvalidPaymentSubmit();
              },
            )}
            onSubmit={handlePaymentSubmit(
              (data) => onSubmit(data, "SavePayment"),
              () => {
                void handleInvalidPaymentSubmit();
              },
            )}
            onSubmitSaveAddMorePayment={handlePaymentSubmit(
              (data) => onSubmit(data, "SaveAddMore"),
              () => {
                void handleInvalidPaymentSubmit();
              },
            )}
            ShowSaveAddMorePaymentButton={true}
            SaveAddMorebuttonLabel="Save & Add More"
            loading={isLoading}
            buttonLabel="Save"
            isSubmitDisabled={isLoading || !isDirty || !isFormValid}
          />
        </FormProvider>
      </div>
      <RightSideModal
        isOpen={modals.payee}
        onClose={() => toggleModal("payee")}
        title="Add Payee"
        buttonlabel="Save"
        formId="AddPayee"
        maxWidth={550}
        isDirty={payeeMethods.formState.isDirty}
        isSubmitDisabled={isLoading || !payeeMethods.formState.isDirty || !payeeMethods.formState.isValid}
      >
        <Card>
          <FormProvider {...payeeMethods}>
            <form id="AddPayee" onSubmit={payeeMethods.handleSubmit(onAddPayeeSubmit)}>
              <div className="row">
                <div className="col-md-12">
                  <InputField
                    name="payeeName"
                    label="Payee Name"
                    required
                    requiredMessage="Payee Name is required"
                    type="text"
                    placeholder="Enter Payee Name"
                    onlyAlphabets
                  />
                </div>
                <div className="col-md-12">
                  <SelectField
                    name="AccountTypeId"
                    label="Account Type"
                    required
                    requiredMessage="Select Account Type"
                    options={accountPayeeTypesOptions}
                    placeholderText="Select Account Type"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </form>
          </FormProvider>
        </Card>
      </RightSideModal>

      <RightSideModal
        isOpen={modals.viewAll}
        onClose={() => toggleModal("viewAll")}
        title="Dues & Transactions"
        isFooterHidden={true}
        formId="DuesAndTransactions"
        maxWidth={480}
      >
        <Card className="p-0 no-bg">
          <DynamicTabs
            tabs={[
              {
                id: "dues",
                label: "Dues",
                onClick: handleTabChange,
                content: <>{renderDuesContent()}</>,
              },
              {
                id: "transactions",
                label: "Transactions",
                onClick: handleTabChange,
                content: renderTransactionsContent(),
              },
            ]}
            defaultActiveTab="dues"
          />
        </Card>
      </RightSideModal>
    </>
  );
};

export default AddPayment;